import React, { SyntheticEvent } from 'react';
import { themes, utils } from 'kb-shared';
import { Auth } from 'aws-amplify';
import Honeybadger from '@honeybadger-io/js';
import styled from 'styled-components';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import Button from '../../components/Button/NewButton';
import TextInput from '../../components/TextInput/NewTextInput';
import { showErrorToast, showSuccessToast } from '../../utilities/notificationUtils';
import CreatePasswordInput from '../Book/components/CreatePasswordInput';

const { isEmailValid, isPasswordValid, isEmailCodeValid, isPasswordMatch } = utils;
const KBContacts = utils.KBContacts;

const FormContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 350px;
`;

const InstructionText = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  margin-bottom: 12px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 40px;
`;

const GoBackLink = styled.a`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  padding-top: 28px;
  padding-bottom: 28px;
  text-decoration: underline;
  color: ${themes.colors.KBNight};
  cursor: pointer;
`;

type Props = RouteComponentProps & {
  onSuccessfulReset: () => void;
  onGoBack: () => void;
  handleUnconfirmed: (email: string) => void;
  updateFormType: (type: string) => void;
  formType: 'request_code' | 'reset_password';
};

type State = {
  email: string;
  password: string;
  confirmPassword: string;
  code: string;
  loading: boolean;
  emailError: boolean;
  passwordError: boolean;
  confirmPasswordError: boolean;
  codeError: boolean;
};

class ForgotPassword extends React.Component<Props, State> {
  initialEmail = () => {
    try {
      // @ts-ignore
      return this.props.location.state.email;
    } catch {
      return '';
    }
  };

  state = {
    email: this.initialEmail(),
    password: '',
    confirmPassword: '',
    code: '',
    loading: false,
    emailError: false,
    passwordError: false,
    confirmPasswordError: false,
    codeError: false
  };

  submitRequestVerificationCode = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!this.validateEmail()) {
      showErrorToast('Email invalid, please check to make sure your address is correct.');
      return;
    }

    try {
      this.setState(() => ({ loading: true }));
      const { sanitizeUsername } = utils;
      const { email } = this.state;
      await Auth.forgotPassword(sanitizeUsername(email));
      this.setState(() => ({ loading: false }));
      this.props.updateFormType('reset_password');
    } catch (error) {
      Honeybadger.notify(error);
      if (error.code === 'UserNotFoundException') {
        this.props.updateFormType('reset_password');
      } else if (error.code === 'LimitExceededException') {
        showErrorToast(
          `You have exceeded the limit for requesting a password reset. Please wait then try again, or contact ${KBContacts.navigatorEmail}`
        );
      } else {
        showErrorToast(
          'You have an account that has not been confirmed, please check your email for a verification code.'
        );
        setTimeout(() => this.props.handleUnconfirmed(this.state.email), 3000);
      }
      this.setState(() => ({ loading: false }));
    }
  };

  // @ts-ignore
  submitChangePassword = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    await this.validateNewPassword();
    if (this.state.codeError) {
      return showErrorToast('Confirmation code incorrect, please check your email.');
    } else if (this.state.emailError) {
      return showErrorToast(
        'Password must be at least 8 characters long and include uppercase and lowercase letters, as well as at least one number and one special character. Both passwords must match.'
      );
    } else if (this.state.passwordError) {
      return showErrorToast(
        'Password format incorrect. Must contain both uppercase and lowercase letters, a special character, and be at least 8 characters long. '
      );
    } else if (this.state.confirmPasswordError) {
      return showErrorToast('Both passwords must match.');
    }

    try {
      this.setState(() => ({ loading: true }));

      const { email, password, code } = this.state;
      const { sanitizeUsername } = utils;
      await Auth.forgotPasswordSubmit(sanitizeUsername(email), code, password);

      showSuccessToast('Reset password successful, please login.');

      setTimeout(() => {
        this.props.onSuccessfulReset();
      }, 3000);
    } catch (error) {
      if (error.code === 'CodeMismatchException') {
        showErrorToast('Confirmation code incorrect, please check your email.');
      } else if (this.state.passwordError) {
        showErrorToast(
          'Password format incorrect. Must contain both uppercase and lowercase letters, a special character, and be at least 8 characters long.'
        );
      } else {
        showErrorToast('Something went wrong. Please try again.');
        Honeybadger.notify(error, 'UncaughtPasswordChangeError');
      }
      this.setState(() => ({ loading: false }));
    }
  };

  validateEmail = () => {
    const { email } = this.state;
    const emailValid = isEmailValid(email);

    this.setState({
      emailError: !emailValid
    });
    return emailValid;
  };

  validateNewPassword = () => {
    const { password, confirmPassword, code } = this.state;
    const passwordValid = isPasswordValid(password);
    const confirmPasswordValid = isPasswordValid(password);
    const passwordMatchValid = isPasswordMatch(password, confirmPassword);
    const codeValid = isEmailCodeValid(code);

    this.setState({
      codeError: !codeValid,
      passwordError: !passwordValid,
      confirmPasswordError: !confirmPasswordValid || !passwordMatchValid
    });
    return Promise.resolve(codeValid && passwordValid && passwordMatchValid && passwordMatchValid);
  };

  renderRequestVerificationCode() {
    const { email, emailError, loading } = this.state;
    return (
      <FormContainer>
        <FormContent>
          <InstructionText>
            Enter the email address you created your account with to receive a verification code
            that will enable you to set a new password.
          </InstructionText>
          <form onSubmit={this.submitRequestVerificationCode} autoComplete="new-password">
            <TextInput
              name={'email'}
              label={'EMAIL ADDRESS'}
              value={email}
              onChange={e => {
                const email = e.currentTarget.value.trim();
                this.setState(() => ({ email }));
              }}
              error={emailError}
              autoComplete="email"
              spellCheck="false"
              placeholder={'janedoe@email.com'}
              inputMode="email"
            />
            <ButtonWrapper>
              <Button
                loading={loading}
                disabled={false}
                type="submit"
                text="REQUEST VERIFICATION CODE"
              />
            </ButtonWrapper>
          </form>
          <GoBackLink onClick={this.props.onGoBack}>Go back</GoBackLink>
        </FormContent>
      </FormContainer>
    );
  }

  renderResetPassword() {
    const {
      email,
      code,
      codeError,
      password,
      confirmPassword,
      passwordError,
      confirmPasswordError,
      loading
    } = this.state;
    return (
      <FormContainer>
        <FormContent>
          <InstructionText>{email}</InstructionText>
          <form onSubmit={this.submitChangePassword} autoComplete="new-password">
            <TextInput
              name={'code'}
              label={'CONFIRMATION CODE'}
              value={code}
              onChange={e => {
                const code = e.currentTarget.value;
                this.setState(() => ({ code }));
              }}
              error={codeError}
              autoComplete="off"
              spellCheck="false"
              placeholder=""
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <CreatePasswordInput
              value={password}
              onChange={({ currentTarget }) => this.setState({ password: currentTarget.value })}
              error={passwordError}
              hasConfirmation
              confirmationValue={confirmPassword}
              onChangeConfirmation={({ currentTarget }) =>
                this.setState({ confirmPassword: currentTarget.value })
              }
              confirmationError={confirmPasswordError}
            />
            <ButtonWrapper>
              <Button loading={loading} disabled={false} type="submit" text="CREATE NEW PASSWORD" />
            </ButtonWrapper>
          </form>
          <GoBackLink onClick={this.props.onGoBack}>Go back</GoBackLink>
        </FormContent>
      </FormContainer>
    );
  }

  render() {
    if (this.props.formType === 'request_code') {
      return this.renderRequestVerificationCode();
    } else {
      return this.renderResetPassword();
    }
  }
}

export default withRouter(ForgotPassword);
